/**
 * admin_pwa_controller.js — Admin > PWA Management (Stimulus).
 *
 * Force PWA Cache Refresh bumps the service worker's version on the server and
 * shows the old and new versions; Check Cache Status shows the worker's
 * current version, age and size. Both answer in the page's result modal.
 * After a refresh the page re-renders through window.chippiePageCheck.refresh(),
 * which waits until the modal is closed, so the cache cards show the new
 * version.
 *
 * Values: refreshUrl (POST), statusUrl (GET).
 * Targets: modal, title, body (the result modal and its parts).
 *
 * Template: templates/admin/pwa_management.html
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = { refreshUrl: String, statusUrl: String };
    static targets = ['modal', 'title', 'body'];

    forceRefresh(event) {
        this._run(event.currentTarget, 'Updating Cache...', this.refreshUrlValue, { method: 'POST' }, (data) => {
            this._show('Success', successHtml(data), 'success');
            window.chippiePageCheck.refresh();
        });
    }

    checkStatus(event) {
        this._run(event.currentTarget, 'Checking...', this.statusUrlValue, {}, (data) => {
            this._show('Cache Status', statusHtml(data.cache_info), 'info');
        });
    }

    _run(button, label, url, options, onSuccess) {
        const original = button.innerHTML;
        button.disabled = true;
        button.textContent = label;
        fetch(url, Object.assign({ credentials: 'same-origin' }, options))
            .then(function (response) { return response.json(); })
            .then(function (data) {
                if (!data.success) throw new Error(data.message || 'Request failed');
                onSuccess(data);
            })
            .catch((error) => this._show('Error', escapeHtml(error.message), 'error'))
            .then(function () {
                button.disabled = false;
                button.innerHTML = original;
            });
    }

    _show(title, html, type) {
        this.titleTarget.textContent = title;
        this.bodyTarget.innerHTML = html;
        const content = this.modalTarget.querySelector('.modal-content');
        content.classList.toggle('border-success', type === 'success');
        content.classList.toggle('border-danger', type === 'error');
        bootstrap.Modal.getOrCreateInstance(this.modalTarget).show();
    }
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
}

function successHtml(data) {
    let html = '<div class="alert alert-success"><i class="bi bi-check-circle me-2"></i>' + escapeHtml(data.message) + '</div>'
        + '<div class="row">'
        + '<div class="col-6"><strong>Old Version:</strong><br><code>' + escapeHtml(data.old_version) + '</code></div>'
        + '<div class="col-6"><strong>New Version:</strong><br><code>' + escapeHtml(data.new_version) + '</code></div>'
        + '</div>';
    if (data.instructions) {
        html += '<h6 class="mt-3">What happens next:</h6><ul>'
            + data.instructions.map(function (line) { return '<li>' + escapeHtml(line) + '</li>'; }).join('')
            + '</ul>';
    }
    return html;
}

function statusHtml(info) {
    const rows = [
        ['Service Worker', info.service_worker_exists
            ? '<span class="text-success">Active</span>' : '<span class="text-danger">Not Found</span>'],
        ['Current Version', '<code>' + escapeHtml(info.current_version || 'Unknown') + '</code>']
    ];
    if (info.last_modified) rows.push(['Last Modified', escapeHtml(new Date(info.last_modified * 1000).toLocaleString())]);
    if (info.file_size) rows.push(['File Size', (info.file_size / 1024).toFixed(1) + ' KB']);
    return '<div class="table-responsive"><table class="table table-sm">'
        + rows.map(function (row) { return '<tr><td><strong>' + row[0] + ':</strong></td><td>' + row[1] + '</td></tr>'; }).join('')
        + '</table></div>';
}
